import {FlywheelComponent} from '#lit/lib/component.js';
import {customElement, property, state} from 'lit/decorators.js';
import type {AlertIntent, AlertSize} from '#lit/components/base/alert/index.js';
import {isPlainObject} from '#lit/utils/index.js';
import {html} from 'lit';
import {repeat} from 'lit/directives/repeat.js';
import {ifDefined} from 'lit/directives/if-defined.js';
import {NavEvents} from '#lit/types/events.js';
import {styleMap} from 'lit/directives/style-map.js';
import styles from './style.scss';

export const COMPONENT_TAG = 'fly-toast';

export enum ActionType {
  ADD_NOTIFICATION = 'notifications:add',
  SAVE_NOTIFICATION = 'notifications:save',
  CLEAR_NOTIFICATION = 'notifications:clear',
  REMOVE_NOTIFICATION = 'notifications:remove',
}

export type Msg =
  | {
      type:
        | ActionType.ADD_NOTIFICATION
        | ActionType.REMOVE_NOTIFICATION
        | ActionType.SAVE_NOTIFICATION;
      payload: Notification;
    }
  | {type: ActionType.CLEAR_NOTIFICATION};

interface Notification {
  type: AlertIntent;
  size?: AlertSize;
  dismissible?: boolean;
  timeout?: number;
  content: string[];
}

@customElement(COMPONENT_TAG)
export default class Toast extends FlywheelComponent {
  @property()
  key = `${new Date().getTime()}-${Math.random()}`;

  @state()
  notifications: Set<Notification> = new Set();

  @state()
  private sideMargin = 0;

  private abortController: AbortController | null = null;

  static get styles() {
    return [styles];
  }

  /**
   * Calculates margin for rendered notification in order to account for the
   * sidenav.
   */
  private setSideMargin() {
    this.sideMargin =
      (document.querySelector('#app > aside') as HTMLElement)?.offsetWidth || 0;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.abortController?.abort();
  }

  load() {
    this.abortController = new AbortController();
    window.addEventListener(
      COMPONENT_TAG,
      (evt: Event) => {
        if (!(evt instanceof CustomEvent)) return;
        const msg = evt.detail as Msg;
        this.action(msg);
      },
      {
        signal: this.abortController?.signal,
      }
    );

    this.notifications = this.getFromStorage();
    sessionStorage.setItem(COMPONENT_TAG, JSON.stringify([]));
    this.requestUpdate();

    window.addEventListener(
      NavEvents.TOGGLE_COLLAPSE,
      () => {
        this.setSideMargin();
      },
      {
        signal: this.abortController?.signal,
      }
    );
  }

  rendered() {
    this.setSideMargin();
  }

  getFromStorage() {
    let data = JSON.parse(
      sessionStorage.getItem(COMPONENT_TAG) || JSON.stringify([])
    );

    data = Array.isArray(data)
      ? (data.filter(isPlainObject) as unknown as Notification[])
      : [];

    const notifications = new Set<Notification>();

    for (const item of data) {
      notifications.add(item);
    }

    return notifications;
  }

  saveToStorage(notification: Notification) {
    const notifications = this.getFromStorage();
    if (!this.notificationExists(notifications, notification)) {
      notifications.add(notification);
    }
    sessionStorage.setItem(
      COMPONENT_TAG,
      JSON.stringify(Array.from(notifications))
    );
  }

  notificationExists(
    notifications: Set<Notification>,
    notification: Notification
  ): boolean {
    for (const n of notifications) {
      if (JSON.stringify(n) === JSON.stringify(notification)) {
        return true;
      }
    }
    return false;
  }

  action(msg: Msg) {
    this.setSideMargin();

    switch (msg.type) {
      case ActionType.ADD_NOTIFICATION: {
        this.notifications.add(msg.payload);
        this.requestUpdate();
        return;
      }

      case ActionType.REMOVE_NOTIFICATION: {
        this.notifications.delete(msg.payload);
        this.requestUpdate();
        return;
      }

      case ActionType.CLEAR_NOTIFICATION: {
        this.notifications.clear();
        this.requestUpdate();
        return;
      }

      case ActionType.SAVE_NOTIFICATION: {
        this.saveToStorage(msg.payload);
        return;
      }

      default: {
        return;
      }
    }
  }

  notificationMarkup(notification: Notification, _idx: number) {
    return html`
      <div class="fly-toast__notification">
        <fly-alert
          ?dismissible=${notification.dismissible ?? true}
          timeout=${ifDefined(notification.timeout)}
          intent=${notification.type}
          size=${ifDefined(notification.size)}
          @closed=${() =>
            this.action({
              type: ActionType.REMOVE_NOTIFICATION,
              payload: notification,
            })}
        >
          <ul class="fly-toast__notification__messages">
            ${repeat(
              notification.content,
              (_, idx: number) => `repeatkey:${COMPONENT_TAG}-${idx}`,
              (message: string) => {
                return html`
                  <li
                    class="fly-toast__notification__message"
                    style="list-style: none;"
                  >
                    ${message}
                  </li>
                `;
              }
            )}
          </ul>
        </fly-alert>
      </div>
    `;
  }

  render() {
    return html`
      <div class="fly-toast">
        <div
          class="fly-toast__notifications"
          style=${styleMap({'margin-left': `${this.sideMargin}px`})}
        >
          ${repeat(
            Array.from(this.notifications),
            (_, idx) => `repeatkey:${COMPONENT_TAG}-${idx}`,
            this.notificationMarkup.bind(this)
          )}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_TAG]: Toast;
  }
}
